import React, { useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import {
  Cloud as CloudIcon,
  Opacity as OpacityIcon,
  ShowChart as ShowChartIcon,
  Thermostat as ThermostatIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { DashboardResponse, Pour2Limit } from '../../../services/pours';
import { maturityShortLabel, maturityShortTitle } from '../../../utils/maturityDisplay';
import { parseUtc } from '../../../utils/datetime';
import { WEATHER_DELAYED_MAX_MS } from '../../../utils/onlineStatus';
import { earliestTime, formatAgo } from '../../../utils/relativeTime';
import { formatWind } from '../../../utils/windSpeed';
import { buildOverlayPanels, metricConfig } from '../overlayModel';
import { chartRangeMs, defaultChartRange, type ChartRangeKey } from './chartDelta';
import {
  OVERLAY_METRIC,
  OVERLAY_NEED,
  chartPickerOptions,
  defaultHiddenOverlaySeries,
  newestReadingAt,
  overlayLocations,
  type MobileChartKey,
} from './mobileDashboardModel';
import { pickerChipSx } from './MobileLocationView';
import { PhoneChart } from './PhoneChart';
import { SOFT_CARD_SX } from './PourStatusStrip';
import { buildPhoneOverlayOptions, PHONE_OVERLAY_HEIGHT } from './phoneChartOptions';
import { useDashboardModel } from './useDashboardModel';
import { useOverlayCharts } from './useChartData';

export interface MobileChartsViewProps {
  pourId: string;
  data: DashboardResponse;
  pourLimits: ReadonlyArray<Pour2Limit>;
  /** The time of the data (the last successful fetch). */
  refNow: number;
  /** Bumped by the page's polls and refreshes: the chart reloads quietly. */
  refreshTick: number;
  /** The picked option, kept by the page across tabs; the first option
   *  with data when unset (or no longer offered). */
  chartKey: MobileChartKey | null;
  onChartKeyChange: (key: MobileChartKey) => void;
}

const ICONS: Record<MobileChartKey, React.ComponentType<SvgIconProps>> = {
  temperature: ThermostatIcon,
  strength: ShowChartIcon,
  maturity: TimelineIcon,
  humidity: OpacityIcon,
  evaporation: CloudIcon,
};

/**
 * The phone dashboard's Charts tab: one overlay chart at a time, picked
 * from the options that have data (Temperature, Strength, TTF / EA, Temp &
 * RH, Evaporation), with the range control and the legend as a list with
 * each line's newest value. Only the
 * shown option's series load, windowed by the range; above 8 locations the
 * chart starts on those needing attention and the 5 hottest.
 */
export const MobileChartsView: React.FC<MobileChartsViewProps> = ({
  pourId,
  data,
  pourLimits,
  refNow,
  refreshTick,
  chartKey,
  onChartKeyChange,
}) => {
  const { t } = useTranslation();
  const model = useDashboardModel(data, pourLimits, refNow);
  const options = useMemo(() => (model ? chartPickerOptions(data, model.flags) : []), [model, data]);
  const key: MobileChartKey | null = chartKey && options.includes(chartKey) ? chartKey : options[0] ?? null;

  const locations = useMemo(
    () => (model && key ? overlayLocations(model.locations, key) : []),
    [model, key],
  );
  const need = key ? OVERLAY_NEED[key] : OVERLAY_NEED.temperature;
  const [range, setRange] = useState<ChartRangeKey>(() =>
    defaultChartRange(earliestTime((data.locations ?? []).map((l) => l.logging_started)), newestReadingAt(data) ?? refNow),
  );
  const overlay = useOverlayCharts(pourId, key ? locations : [], need, refreshTick, data.pour?.status, range);

  // Legend choices per option; above 8 locations the chart starts on the
  // locations needing attention and the 5 hottest.
  const [hiddenByKey, setHiddenByKey] = useState<Partial<Record<MobileChartKey, string[]>>>({});
  const defaultHidden = useMemo(
    () => (model ? defaultHiddenOverlaySeries(locations, model.healthById) : new Set<string>()),
    [locations, model],
  );
  const chosen = key ? hiddenByKey[key] : undefined;
  const hidden = useMemo(() => (chosen ? new Set(chosen) : defaultHidden), [chosen, defaultHidden]);

  const meta = model?.meta;
  const unit = model?.strengthUnit ?? '';
  const metric = key ? OVERLAY_METRIC[key] : 'temperature';
  const build = useMemo(() => {
    if (!key || !meta || overlay.charts.length === 0) return null;
    const panel = buildOverlayPanels(overlay.charts, metric, meta, unit)[0];
    if (!panel) return null;
    return {
      panel,
      ...buildPhoneOverlayOptions(panel, metricConfig(metric, meta, unit), {
        limits: key === 'temperature' ? pourLimits : [],
        evaLimits: key === 'evaporation' ? overlay.charts[0]?.chart.evaLimit : null,
        rangeMs: chartRangeMs(range),
        hidden,
        timezoneIana: data.pour?.timezone_iana,
        t,
      }),
    };
  }, [key, meta, unit, metric, overlay.charts, pourLimits, range, hidden, data.pour?.timezone_iana, t]);

  if (!model) return null;

  if (options.length === 0 || !key || !meta) {
    return (
      <Box data-testid="mobile-charts-view" sx={{ ...SOFT_CARD_SX, p: 2 }}>
        <Typography sx={{ fontSize: '0.875rem' }}>
          {model.locations.length === 0
            ? t('mobile.dashboard.location.noLocations', { defaultValue: 'No monitoring locations are set up for this pour.' })
            : t('mobile.dashboard.chartsTab.afterFirstReading', { defaultValue: 'Charts appear after the first reading' })}
        </Typography>
      </Box>
    );
  }

  const label = (k: MobileChartKey): string => {
    switch (k) {
      case 'strength':
        return t('mobile.dashboard.chartsTab.strength', { defaultValue: 'Strength' });
      case 'maturity':
        return maturityShortLabel(meta);
      case 'humidity':
        return t('mobile.dashboard.chartsTab.humidity', { defaultValue: 'Temp & RH' });
      case 'evaporation':
        return t('mobile.dashboard.chartsTab.evaporation', { defaultValue: 'Evaporation' });
      default:
        return t('mobile.dashboard.chartsTab.temperature', { defaultValue: 'Temperature' });
    }
  };
  const title = (() => {
    switch (key) {
      case 'strength':
        return t('mobile.dashboard.chartsTab.strengthTitle', { defaultValue: 'Strength ({{unit}})', unit });
      case 'maturity':
        return maturityShortTitle(meta);
      case 'humidity':
        return t('mobile.dashboard.chartsTab.humidityTitle', { defaultValue: 'Temperature (°C) and humidity (%RH)' });
      case 'evaporation':
        return t('mobile.dashboard.chartsTab.evaporationTitle', { defaultValue: 'Evaporation (kg/m²/h)' });
      default:
        return t('mobile.dashboard.chartsTab.temperatureTitle', { defaultValue: 'Temperature (°C)' });
    }
  })();

  const weather = data.weather;
  // The reading's own time (site_state replies): its age is shown, and past
  // the weather-station Offline band (60 minutes) the line is greyed, so an
  // old reading never reads as "now".
  const weatherAt = weather?.at ? parseUtc(weather.at).getTime() : NaN;
  const weatherOld = !Number.isNaN(weatherAt) && refNow - weatherAt > WEATHER_DELAYED_MAX_MS;
  const siteLine = key === 'evaporation' && weather
    && (weather.temp != null || weather.humidity != null || weather.wind_speed != null)
    ? t('mobile.dashboard.strip.siteNow', {
        defaultValue: 'Site now {{values}}',
        values: [
          weather.temp != null ? `${weather.temp.toFixed(1)} °C` : null,
          weather.humidity != null ? `${weather.humidity.toFixed(0)} %RH` : null,
          weather.wind_speed != null ? formatWind(weather.wind_speed, data.pour?.wind_speed_unit) : null,
          Number.isNaN(weatherAt) ? null : formatAgo(weatherAt, refNow, t),
        ].filter(Boolean).join(' · '),
      })
    : null;

  const emptyText = overlay.error
    ?? (key === 'strength' && build?.panel.emptyText ? build.panel.emptyText : undefined);

  return (
    <Box data-testid="mobile-charts-view" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box
        role="group"
        aria-label={t('mobile.dashboard.chartsTab.picker', { defaultValue: 'Chart' })}
        sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 0.75 }}
      >
        {options.map((k) => {
          const Icon = ICONS[k];
          const selected = k === key;
          return (
            <Box key={k} sx={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
              <Chip
                icon={<Icon aria-hidden />}
                label={label(k)}
                clickable
                aria-pressed={selected}
                data-testid={`chart-option-${k}`}
                onClick={() => onChartKeyChange(k)}
                sx={pickerChipSx(selected)}
              />
            </Box>
          );
        })}
      </Box>
      {siteLine && (
        <Typography
          sx={{ fontSize: '0.875rem', fontWeight: 600, color: weatherOld ? 'text.disabled' : undefined }}
          data-testid="charts-site-line"
        >
          {siteLine}
        </Typography>
      )}
      <Box data-testid="overlay-chart-card" sx={{ ...SOFT_CARD_SX, p: 2, minWidth: 0 }}>
        <PhoneChart
          title={title}
          newestAt={build?.latest ?? null}
          range={range}
          onRangeChange={setRange}
          options={build?.options ?? null}
          legend={build?.legend}
          hidden={hidden}
          onToggleSeries={(id) =>
            setHiddenByKey((h) => {
              const list = h[key] ?? Array.from(hidden);
              return { ...h, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
            })}
          onShowAllSeries={() => setHiddenByKey((h) => ({ ...h, [key]: [] }))}
          height={PHONE_OVERLAY_HEIGHT}
          legendStyle="list"
          loading={overlay.firstLoading}
          empty={!overlay.firstLoading && (!build || !build.hasData)}
          emptyText={emptyText}
          footer={key === 'strength' && build?.panel.caption ? (
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.35, color: 'text.secondary', mt: 1 }} data-testid="strength-range-caption">
              {build.panel.caption}
            </Typography>
          ) : undefined}
        />
      </Box>
    </Box>
  );
};

export default MobileChartsView;
